import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, ArrowRight, User, ArrowUpRight } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { PageLoader } from '@/components/ui/Spinner';
import { useBlogPost, useBlogPosts } from '@/hooks/useBlog';
import { useTranslation } from '@/features/i18n';
import { formatDate } from '@/lib/utils';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, error } = useBlogPost(slug || '');
  const { data: relatedPosts } = useBlogPosts({ limit: 3 });
  const { t } = useTranslation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#edf1f5]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">
            {t.blog.notFound.title}
          </h1>
          <p className="text-[#666] mb-6">
            {t.blog.notFound.description}
          </p>
          <Link to="/blog" className="ninito-btn ninito-btn-primary">
            {t.blog.backToBlog}
          </Link>
        </div>
      </div>
    );
  }

  const otherPosts = relatedPosts?.posts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt || ''}
        keywords={post.seo_keywords || ''}
        ogImage={post.og_image_url || post.cover_image_url || ''}
        ogType="article"
      />

      <article>
        {/* Header - ninito style */}
        <header className="bg-white pt-32 pb-16 lg:pt-40 lg:pb-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/blog"
                className="inline-flex items-center text-[#666] hover:text-black transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.blog.backToBlog}
              </Link>

              {post.category && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#0078ff]/10 text-[#0078ff] text-sm font-medium mb-6">
                  {post.category}
                </span>
              )}

              <h1 className="text-[32px] md:text-[40px] lg:text-[52px] font-semibold text-black leading-[1.1] tracking-[-0.02em]">
                {post.title}
              </h1>

              <div className="mt-8 flex flex-wrap items-center gap-6 text-[#666]">
                {post.author_name && (
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2 text-[#999]" />
                    {post.author_name}
                  </div>
                )}
                {post.published_at && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-[#999]" />
                    {formatDate(post.published_at)}
                  </div>
                )}
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-[#999]" />
                  {post.reading_time} {t.blog.readTime}
                </div>
              </div>
            </motion.div>
          </div>
        </header>

        {/* Cover image - ninito style */}
        {post.cover_image_url && (
          <div className="max-w-[1100px] mx-auto px-5 md:px-10 -mt-4">
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              src={post.cover_image_url}
              alt={post.title}
              className="w-full aspect-[16/9] object-cover rounded-[28px]"
            />
          </div>
        )}

        {/* Content - ninito style */}
        <div className="py-16 lg:py-24 bg-white">
          <div className="max-w-[720px] mx-auto px-5 md:px-10">
            {post.excerpt && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-xl text-[#666] mb-10 font-medium leading-relaxed"
              >
                {post.excerpt}
              </motion.p>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="prose prose-lg max-w-none [&_h2]:text-black [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-[28px] [&_h2]:mt-12 [&_h2]:mb-6 [&_h3]:text-black [&_h3]:font-semibold [&_p]:text-[#666] [&_p]:leading-relaxed [&_li]:text-[#666] [&_a]:text-[#0078ff] [&_a]:no-underline hover:[&_a]:underline [&_strong]:text-black [&_blockquote]:border-l-[#0078ff] [&_blockquote]:bg-[#edf1f5] [&_blockquote]:rounded-r-2xl [&_blockquote]:py-4 [&_blockquote]:px-6 [&_code]:bg-[#edf1f5] [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-black"
              dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
            />

            {/* Tags - ninito style */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-16 pt-8 border-t border-black/10">
                <h3 className="text-sm font-medium text-[#999] uppercase tracking-wider mb-4">
                  {t.blog.tags}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-[#edf1f5] text-[#666] text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related posts - ninito style */}
        {otherPosts && otherPosts.length > 0 && (
          <section className="ninito-section bg-[#edf1f5]">
            <div className="ninito-container">
              <h2 className="text-2xl font-semibold text-black tracking-tight mb-10">
                {t.blog.relatedPosts}
              </h2>
              <div className="grid md:grid-cols-3 gap-7">
                {otherPosts.map((relatedPost, index) => (
                  <motion.article
                    key={relatedPost.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link to={`/blog/${relatedPost.slug}`} className="block group">
                      <div className="ninito-card h-full p-0 overflow-hidden ninito-hover-lift">
                        {relatedPost.cover_image_url && (
                          <div className="aspect-[16/10] overflow-hidden rounded-t-[28px]">
                            <img
                              src={relatedPost.cover_image_url}
                              alt={relatedPost.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <h3 className="text-lg font-semibold text-black group-hover:text-[#0078ff] transition-colors line-clamp-2">
                            {relatedPost.title}
                          </h3>
                          <p className="mt-2 text-[#666] text-sm line-clamp-2">
                            {relatedPost.excerpt}
                          </p>
                          <div className="mt-4 flex items-center text-[#0078ff] text-sm font-medium">
                            Les mer
                            <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA - ninito dark style */}
        <section className="ninito-cta">
          <div className="relative ninito-container">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em]"
            >
              {t.blog.cta.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="mt-5 text-lg md:text-xl text-[#999] max-w-[600px] mx-auto"
            >
              {t.blog.cta.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-10"
            >
              <Link to="/contact" className="ninito-btn ninito-btn-white ninito-btn-lg group">
                {t.common.contactUs}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </article>
    </>
  );
}
